"use client";

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { AppSidebar } from "@/components/navigation/AppSidebar/AppSidebar";
import { eventNavGroups } from "@/components/navigation/AppSidebar/eventNavConfig";
import { filterNavGroups } from "@/components/navigation/AppSidebar/filterNavGroups";
import { AppTopNav } from "@/components/navigation/AppTopNav/AppTopNav";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useIsTablet } from "@/hooks/use-mobile";
import { useUserCodenames } from "@/hooks/useUserCodenames";

interface ProtectedLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout shown to authenticated users — sidebar + topbar shell.
 * The proxy guarantees that anything rendered inside this layout has a valid
 * session, so we don't need to gate the children here.
 */
export default function ProtectedLayout({ children }: ProtectedLayoutProps) {
  const isTablet = useIsTablet();
  const [open, setOpen] = useState(true);
  const userCodenames = useUserCodenames();

  useEffect(() => {
    setOpen(!isTablet);
  }, [isTablet]);

  const filteredNavGroups = useMemo(
    () => filterNavGroups(eventNavGroups, userCodenames),
    [userCodenames],
  );

  return (
    <SidebarProvider
      open={open}
      onOpenChange={setOpen}
      className="h-svh overflow-hidden print:h-auto print:overflow-visible"
      style={{ "--sidebar-width-icon": "4rem" } as React.CSSProperties}
    >
      <AppSidebar navGroups={filteredNavGroups} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden print:overflow-visible">
        {/* Chrome is hidden in print so the page content (e.g. an invoice) prints alone */}
        <div className="print:hidden">
          <AppTopNav />
        </div>
        <main className="flex-1 overflow-y-auto bg-neutral-50 print:overflow-visible print:bg-white">
          {children}
        </main>
      </div>
    </SidebarProvider>
  );
}
